import * as React from 'react'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'

import { Applicant, MultisectorInfo, ProviderData, ThirdPartyData } from 'store/dashboard/types'
import { useState } from 'react'
import SouthIcon from '@mui/icons-material/South'
import LinkIcon from '@mui/icons-material/Link'
import { Box, Button, Collapse, Divider, Link, MenuItem, Stack, Tooltip } from '@mui/material'
import { ecolors } from 'containers/app/theme'
import OpenCloseButton from 'components/openCloseButton/OpenCloseButton'
import SectionContainer from '../../components/SectionContainer'
import SectionHeader from '../../components/SectionHeader'
import SectionSubTitle from '../../components/SectionSubtitle'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import AddCircleIcon from '@mui/icons-material/AddCircle'

type Props = {
  index: number
  subgroupCode: string
  multisectorInfo: MultisectorInfo
  multisectorApplicants: Applicant[]
  thirdPartyDataList: ThirdPartyData[] | null
  benchmarkApplicants: Applicant[]
  setBenchmarkApplicants: (applicants: Applicant[]) => void
  showZeroSectorVals: boolean
}

type SectorData = {
  applicant: Applicant
  company: string
  ipFootprint: string | number
  ipSimilarity: number
  ipWeight: any
  purePlayRatio: number
  revPerEmployee: any
  ticker: string | null
  website?: string | null
}

interface Column {
  id: keyof SectorData
  label: string
  minWidth?: number
  align?: 'left' | 'center'
  format?: (value: any) => any
}

export default function SectorSection({
  index,
  subgroupCode,
  multisectorInfo,
  multisectorApplicants,
  thirdPartyDataList,
  benchmarkApplicants,
  setBenchmarkApplicants,
  showZeroSectorVals,
}: Props) {
  const subgroupInfo = multisectorInfo.subgroups_info.find(sub => sub.code === subgroupCode)
  const subgroupTitle = subgroupInfo?.claude_main_title || subgroupInfo?.text || subgroupCode
  const sectorTitle = subgroupTitle
  const multisectorDataBySector = multisectorApplicants.filter(app => app.subgroup === subgroupCode)

  const [sectionOpened, setSectionOpened] = useState(true)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(15)

  const parseNumber = (value: any): number => {
    if (typeof value === 'number') return value
    if (typeof value === 'string') {
      // Fix cases like 3,07394E+11
      const cleaned = value.replace(',', '.').replace(/\s/g, '')
      const parsed = parseFloat(cleaned)
      return isNaN(parsed) ? 0 : parsed
    }
    return 0
  }

  const isApplicantSelected = (applicant: Applicant) =>
    benchmarkApplicants.some(a => a.applicant_cleaned === applicant.applicant_cleaned)

  const toggleBenchmarkApplicant = (applicant: Applicant) => {
    if (isApplicantSelected(applicant)) {
      console.log('Remove applicant to benchmark:', applicant.applicant_original_name)
      //Remove applicant from benchmark
      const updated = benchmarkApplicants.filter(a => a.applicant_cleaned !== applicant.applicant_cleaned)
      setBenchmarkApplicants(updated)
    } else {
      console.log('Add applicant to benchmark:', applicant.applicant_original_name)
      const updated = [...benchmarkApplicants, applicant]
      setBenchmarkApplicants(updated)
    }
  }

  const getRevPerEmployee = (tpProvider: ProviderData) => {
    const revenue = parseNumber(tpProvider?.revenue)
    const employees = parseNumber(tpProvider?.employee)
    if (revenue > 0 && employees > 0) {
      return Math.round(revenue / employees / 1000)
    }
    return '-'
  }
  const getWeightData = (value: number) => {
    if (value && value > 0) return value
    else return '-'
  }

  // const getFootprint = (score: string | number) => {
  //   if (comparison_score)
  //     if (typeof score == 'string') return score
  //     else return score.toFixed()
  // }

  const getFootprint = (applicant: Applicant) => {
    if (applicant.comparison_score) {
      if (typeof applicant.comparison_score == 'string') return applicant.comparison_score
      else return applicant.comparison_score.toFixed()
    } else return '-'
  }

  const getSectorData = (multisectorDataBySector: Applicant[], thirdPartyDataList: ThirdPartyData[] | null) => {
    const sectorDataList: SectorData[] = multisectorDataBySector.map(applicant => {
      const tpd = thirdPartyDataList && thirdPartyDataList.filter(tpd => tpd.applicant_cleaned === applicant.applicant_cleaned)[0]

      let tpProvider: ProviderData | null = null

      if (tpd) {
        if (tpd.owler.length) {
          tpProvider = tpd.owler[0]
        } else if (tpd.yfinance_nasdaq.length) {
          tpProvider = tpd.yfinance_nasdaq[0]
        } else if (tpd.yfinance_eu.length) {
          tpProvider = tpd.yfinance_eu[0]
        }
      }

      const sd: SectorData = {
        applicant: applicant,
        company: applicant.applicant_original_name,
        ipFootprint: getFootprint(applicant),
        ipSimilarity: Number(applicant.intersection_score_sum.toFixed()),
        // ipWeight: getWeightData(applicant.whales_patents_in_categories),
        // purePlayRatio: Number(applicant.whales_patent_in_percent.toFixed(2)),
        ipWeight: getWeightData(applicant.whales_patents_in_category),
        purePlayRatio: Number(applicant.whales_patent_in_percent_category.toFixed(2)),
        revPerEmployee: tpProvider && getRevPerEmployee(tpProvider),
        ticker: tpProvider && tpProvider.ticker,
        website: tpProvider && tpProvider?.company_domain,
      }
      return sd
    })
    return sectorDataList
  }

  const sectorDataList: SectorData[] = getSectorData(multisectorDataBySector, thirdPartyDataList)

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value)
    setPage(0)
  }

  const getKey = (item: SectorData) => subgroupCode + item.company + item.applicant.pos

  const columns: Column[] = [
    { id: 'company', label: 'Company', format: value => value.toUpperCase() },
    { id: 'ipSimilarity', label: 'IP Similarity' },
    { id: 'ipWeight', label: 'IP Weight' },
    { id: 'purePlayRatio', label: 'Pure Play %' },
    { id: 'ipFootprint', label: 'IP Footprint' },
    { id: 'revPerEmployee', label: 'Rev / Empl. $`000' },
    { id: 'ticker', label: 'Ticker' },
    {
      id: 'website',
      label: 'Website',
      align: 'center',
      format: value => {
        {
          value ? (
            <Link href={'http://' + value} target="_blank">
              <LinkIcon sx={{ fontSize: 12 }} />
            </Link>
          ) : (
            <></>
          )
        }
      },
    },
  ]

  const Header = () => {
    return (
      <SectionHeader title={'Sector ' + index}>
        <Stack direction="row" alignItems="center">
          <OpenCloseButton onClick={() => setSectionOpened(!sectionOpened)} opened={sectionOpened} />
        </Stack>
      </SectionHeader>
    )
  }

  return (
    <SectionContainer>
      {Header()}
      <Collapse in={sectionOpened}>
        <Paper variant="outlined" sx={{ borderRadius: '20px', m: 3, mt: 0 }}>
          <SectionSubTitle title={sectorTitle}>
            {subgroupInfo?.claude_description && (
              <Stack sx={{ color: ecolors.midgrey, fontSize: '12px' }}>
                <Tooltip title={subgroupInfo?.claude_description + ` (${subgroupInfo?.code})`}>
                  <Button sx={{ textTransform: 'none', color: '#848fc4' }}>Detailed description</Button>
                </Tooltip>
              </Stack>
            )}
          </SectionSubTitle>
          <Divider />
          <TableContainer sx={{ maxHeight: 700 }}>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  <TableCell key="select" />
                  {columns.map(column => (
                    <TableCell key={column.id} align={column.align} style={{ minWidth: column.minWidth }}>
                      {column.label}
                      {column.id === 'ipSimilarity' && <SouthIcon fontSize="inherit" />}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {sectorDataList &&
                  sectorDataList
                    .filter(row => showZeroSectorVals || row.purePlayRatio > 0)
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => {
                      const applicant = row.applicant
                      const isSelected = isApplicantSelected(applicant)
                      return (
                        <TableRow hover role="checkbox" tabIndex={-1} key={getKey(row) + index}>
                          <TableCell sx={{ padding: '1px 10px' }}>
                            {isSelected ? (
                              <AddCircleIcon
                                sx={{ m: 0, color: 'primary.main', cursor: 'pointer' }}
                                fontSize="small"
                                onClick={() => toggleBenchmarkApplicant(applicant)}
                              />
                            ) : (
                              <AddCircleOutlineIcon
                                sx={{ m: 0, color: 'grey.500', cursor: 'pointer' }}
                                fontSize="small"
                                onClick={() => toggleBenchmarkApplicant(applicant)}
                              />
                            )}
                          </TableCell>
                          {columns.map(column => {
                            const value = row[column.id]
                            return (
                              <TableCell key={column.id} align={column.align} sx={{ padding: '11px 16px' }}>
                                {column.format ? column.format(value) : value}
                              </TableCell>
                            )
                          })}
                        </TableRow>
                      )
                    })}
              </TableBody>
            </Table>
            {multisectorDataBySector.length === 0 && (
              <Box sx={{ m: 2 }}>
                No results. Reasons could be: “whales_patent_in_percent” is zero, ‘whales_categories_count’ is less than 2 or
                research is TRUE, or whatever filter is applyed. Check multisector table bellow to validate the reason.
              </Box>
            )}
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[15, 30, 100]}
            component="div"
            count={multisectorDataBySector.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Collapse>
    </SectionContainer>
  )
}
